import { useCallback, useState } from 'react';
import type { AttributeDefinition, Category } from '@/api/schemas/catalog-taxonomy';
import type {
  AttributeDraftPatch,
  CategoryDraftPatch,
  CreatedCategory,
  SetupState,
} from '@/modules/catalog/pages/setup-page/types';
import type { ManualAttributeInput, ManualCategoryInput } from '@/modules/catalog/pages/setup-page/types';
import {
  runCreateAttributes,
  runCreateCategories,
  runCreateManualAttribute,
  runCreateManualCategory,
  runSuggestAttributes,
  runSuggestCategories,
} from '@/modules/catalog/pages/setup-page/parts/actions';

/**
 * THE PAGE'S CONTROLLER.
 *
 * One state value, one updater, and the actions that write through it. The
 * actions themselves live in `parts/actions.ts` as plain functions, so this
 * file stays a short list of what the page can do rather than a page's worth
 * of logic.
 */
export type SetupController = {
  readonly state: SetupState;
  /** Whether the assistant may be asked for a proposal right now. */
  readonly canSuggest: boolean;
  readonly setInstructions: (value: string) => void;
  readonly setTargetCategoryId: (value: string) => void;
  readonly updateCategory: (id: string, patch: CategoryDraftPatch) => void;
  readonly updateAttribute: (id: string, patch: AttributeDraftPatch) => void;
  readonly suggestCategories: () => void;
  readonly suggestAttributes: () => void;
  readonly createSelectedCategories: () => void;
  readonly createSelectedAttributes: () => void;
  readonly createManualCategory: (input: ManualCategoryInput) => void;
  readonly createManualAttribute: (input: ManualAttributeInput) => void;
};

const INITIAL_STATE: SetupState = {
  instructions: '',
  categoryProposal: null,
  attributeProposal: null,
  categoryDrafts: [],
  attributeDrafts: [],
  createdCategories: [],
  targetCategoryId: '',
  categoryFailure: null,
  attributeFailure: null,
  manualCategoryOutcome: null,
  manualAttributeOutcome: null,
  isWorking: false,
};

export function useSetup(params: {
  readonly categories: ReadonlyArray<Category>;
  readonly dictionary: ReadonlyArray<AttributeDefinition>;
  readonly aiReady: boolean;
  readonly onCategoriesChanged: () => void;
}): SetupController {
  const [state, setState] = useState<SetupState>(INITIAL_STATE);
  const patch = useCallback(
    (updater: (previous: SetupState) => SetupState): void => setState(updater),
    [],
  );

  const { categories, dictionary, aiReady, onCategoriesChanged } = params;
  const target = targetOf(state);
  const targetLabel = targetLabelOf(state, categories);

  return {
    state,
    canSuggest: aiReady && state.instructions.trim().length >= 3 && !state.isWorking,
    setInstructions: (value: string) => patch((prev) => ({ ...prev, instructions: value })),
    setTargetCategoryId: (value: string) => patch((prev) => ({ ...prev, targetCategoryId: value })),
    updateCategory: (id: string, value: CategoryDraftPatch) =>
      patch((prev) => ({
        ...prev,
        categoryDrafts: prev.categoryDrafts.map((row) =>
          row.id === id ? { ...row, ...value } : row,
        ),
      })),
    updateAttribute: (id: string, value: AttributeDraftPatch) =>
      patch((prev) => ({
        ...prev,
        attributeDrafts: prev.attributeDrafts.map((row) =>
          row.id === id ? { ...row, ...value } : row,
        ),
      })),
    suggestCategories: () => {
      void runSuggestCategories({ instructions: state.instructions, existingCategories: categories, patch });
    },
    suggestAttributes: () => {
      void runSuggestAttributes({
        instructions: state.instructions,
        targetCategoryId: state.targetCategoryId,
        existingAttributes: dictionary,
        patch,
      });
    },
    createSelectedCategories: () => {
      void runCreateCategories({ drafts: state.categoryDrafts, patch, onSaved: onCategoriesChanged });
    },
    createSelectedAttributes: () => {
      void runCreateAttributes({ drafts: state.attributeDrafts, target, targetLabel, patch });
    },
    createManualCategory: (input: ManualCategoryInput) => {
      void runCreateManualCategory({ input, patch, onSaved: onCategoriesChanged });
    },
    createManualAttribute: (input: ManualAttributeInput) => {
      void runCreateManualAttribute({ input, target, targetLabel, patch });
    },
  };
}

/**
 * The picked category, when it is one THIS PAGE created.
 *
 * Only a category this page created may be written to: the attribute set is a
 * full-set replace, and the API exposes no read of a category's current
 * attributes, so replacing an existing category's set could destroy them.
 */
function targetOf(state: SetupState): CreatedCategory | null {
  if (state.targetCategoryId === '') return null;
  return state.createdCategories.find((c) => c.categoryId === state.targetCategoryId) ?? null;
}

/** The name of the picked category, created or not, for the message. */
function targetLabelOf(
  state: SetupState,
  categories: ReadonlyArray<Category>,
): string | null {
  if (state.targetCategoryId === '') return null;
  const created = state.createdCategories.find((c) => c.categoryId === state.targetCategoryId);
  if (created !== undefined) return created.name;
  return categories.find((c) => c.categoryId === state.targetCategoryId)?.name ?? null;
}
